import React, { useEffect, useState } from 'react';

import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';

import { useThemeContext } from 'global/ThemeContext/ThemeContext';
import { City, fetchCitiesByState, fetchStates, State } from 'services/geoService';

type Props = {
  value: string; // Formato: "Estado | Cidade" (ex: "São Paulo | São Paulo") - APENAS para exibição
  onChange: (value: string, extras?: { state: string; city: string }) => void; // ✅ NOVO: enviar extras
  disabled?: boolean;
};

/**
 * Componente para seleção de Estado + Cidade nas condições de automação
 * Similar ao GeoField do DynamicFormGroup, mas adaptado para conditions
 * ✅ AGORA: Envia state e city separados no extras
 */
export const GeoConditionInput: React.FC<Props> = ({ value, onChange, disabled = false }) => {
  const theme = useThemeContext();
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [selectedStateValue, setSelectedStateValue] = useState('');
  const [selectedCityValue, setSelectedCityValue] = useState('');
  const [loading, setLoading] = useState(false);

  // Carregar estados ao montar
  useEffect(() => {
    const loadStates = async () => {
      try {
        setLoading(true);
        const fetchedStates = await fetchStates();
        setStates(fetchedStates);
      } catch (error) {
        console.error('Erro ao carregar estados:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStates();
  }, []); // Carregar apenas uma vez

  // ✅ NOVO: Hidratar valor quando value mudar (modo edição)
  useEffect(() => {
    const hydrateValue = async () => {
      if (!value || !value.includes(' | ') || states.length === 0) return;

      console.log('🌍 Hidratando GeoConditionInput com valor:', value);

      const [stateLabel, cityLabel] = value.split(' | ');
      const state = states.find(s => s.label === stateLabel);

      if (state) {
        setSelectedStateValue(state.value);
        console.log('✅ Estado selecionado:', state.label);

        try {
          // Carregar cidades do estado
          const fetchedCities = await fetchCitiesByState(state.value);
          setCities(fetchedCities);

          const city = fetchedCities.find(c => c.label === cityLabel);
          if (city) {
            setSelectedCityValue(city.value);
            console.log('✅ Cidade selecionada:', city.label);
          }
        } catch (error) {
          console.error('❌ Erro ao carregar cidades:', error);
        }
      }
    };

    hydrateValue();
  }, [value, states]); // ✅ Reagir a mudanças no value

  // Quando seleciona estado
  const handleStateChange = async (event: SelectChangeEvent<string>) => {
    const stateValue = event.target.value;
    setSelectedStateValue(stateValue);
    setSelectedCityValue(''); // Limpar cidade

    try {
      const fetchedCities = await fetchCitiesByState(stateValue);
      setCities(fetchedCities);
    } catch (error) {
      console.error('Erro ao carregar cidades:', error);
    }
  };

  // Quando seleciona cidade
  const handleCityChange = (event: SelectChangeEvent<string>) => {
    const cityValue = event.target.value;
    setSelectedCityValue(cityValue);

    // Montar valor final: "Estado | Cidade" (para exibição)
    const state = states.find(s => s.value === selectedStateValue);
    const city = cities.find(c => c.value === cityValue);

    if (state && city) {
      const geoValue = `${state.label} | ${city.label}`;
      console.log('🌍 Geo valor selecionado:', geoValue);
      console.log('🌍 Geo extras:', { state: state.label, city: city.label });

      // ✅ NOVO: Passar extras com state e city separados
      onChange(geoValue, { state: state.label, city: city.label });
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
      <FormControl size="small" fullWidth>
        <InputLabel>Estado</InputLabel>
        <Select
          label="Estado"
          value={selectedStateValue}
          onChange={handleStateChange}
          disabled={disabled || loading}
        >
          {states.map(state => (
            <MenuItem key={state.value} value={state.value}>
              {state.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" fullWidth>
        <InputLabel>Cidade</InputLabel>
        <Select
          label="Cidade"
          value={selectedCityValue}
          onChange={handleCityChange}
          disabled={disabled || !selectedStateValue || cities.length === 0}
        >
          {cities.map(city => (
            <MenuItem key={city.value} value={city.value}>
              {city.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {loading && (
        <Typography variant="caption" sx={{ color: theme.text.secondary }}>
          Carregando estados...
        </Typography>
      )}
    </Box>
  );
};
